import React, { useEffect, useState } from "react";
import { AssetInventory, supplierFormData } from "../../../types/api";
import {
  useDepartmentStore,
  useAssetInventoryStore,
  useUtilityAssetTypeStore,
} from "../../../store/store";
import { toast } from "react-toastify";
import { getAllSuppliers } from "../../../service/api";
import {
  useLocationStore,
  useManufacturerListStore,
} from "../../../store/masterDataStore";

interface AddAssetInventoryProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: AssetInventory) => void;

  mode: "create" | "edit" | "view";
  initialData?: AssetInventory | null;
}

const assetTypes = [
  "HVAC",
  "Generator",
  "Water Pump",
  "Electrical Panel",
  "Fire Safety System",
  "Elevator",
  "Plumbing System",
  "Security System",
  "Other",
];

const AddAssetInventory: React.FC<AddAssetInventoryProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  mode,
}) => {
  const { assetInventory } = useAssetInventoryStore();
  const [validationError, setValidationError] = useState<string>("");
  const [suppliers, setSuppliers] = useState<supplierFormData[]>([]);

  const defaultFormData: AssetInventory = {
    name: "",
    type: "",
    location: "",
    purchaseDate: "",
    InstallationDate: "",
    manufacturer: "",
    model: "",
    warrantyExpiry: "",
    maintenanceVendor: "",
    status: "Active",
    attachments: [],
    createdAt: "",
    updatedAt: "",
  };

  const [formData, setFormData] = useState<AssetInventory>(defaultFormData);
  const { departments, fetchAllDepartments } = useDepartmentStore();
  const { fetchAllLocations, allLocations } = useLocationStore();
  const { manufacturerList, fetchAllManufacturerList } =
    useManufacturerListStore();
  const { utilityAssetTypes, fetchUtilityAssetTypes } =
    useUtilityAssetTypeStore();
  // Generate assetId (UT001, UT002, etc.)
  const generateAssetId = () => {
    if (!assetInventory || assetInventory.length === 0) {
      return "AST001";
    }

    const existingIds = assetInventory
      .map((asset) => asset.assetId)
      .filter((id) => id && id.startsWith("AST"))
      .map((id) => {
        const match = id?.match(/AST(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      });

    const maxNumber = existingIds.length > 0 ? Math.max(...existingIds) : 0;
    const nextNumber = maxNumber + 1;
    return `AST${nextNumber.toString().padStart(3, "0")}`;
  };

  useEffect(() => {
    fetchAllDepartments();
    fetchAllLocations();
    fetchAllManufacturerList();
    fetchUtilityAssetTypes();
    // Fetch suppliers
    const fetchSuppliers = async () => {
      try {
        const res = await getAllSuppliers();
        setSuppliers(res.data.data || []);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      }
    };
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (initialData && (mode === "edit" || mode === "view")) {
      // Handle migration from old manufacturerAndModel to separate fields
      const migratedData = { ...initialData };
      if (
        (initialData as any).manufacturerAndModel &&
        !initialData.manufacturer
      ) {
        const parts = (initialData as any).manufacturerAndModel.split(" ");
        migratedData.manufacturer = parts[0] || "";
        migratedData.model = parts.slice(1).join(" ") || "";
      }
      setFormData(migratedData);
    } else if (mode === "create") {
      const newFormData = {
        ...defaultFormData,
        assetId: generateAssetId(),
      };
      setFormData(newFormData);
    }
  }, [initialData, mode, isOpen, assetInventory]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");

    // Validate required fields (fields with red asterisks)
    if (!formData.name?.trim()) {
      setValidationError("Name is required");
      toast.error("Name is required");
      return;
    }

    if (!formData.type?.trim()) {
      setValidationError("Type is required");
      toast.error("Type is required");
      return;
    }

    if (!formData.location?.trim()) {
      setValidationError("Location is required");
      toast.error("Location is required");
      return;
    }

    if (!formData.InstallationDate?.trim()) {
      setValidationError("Installation Date is required");
      toast.error("Installation Date is required");
      return;
    }

    if (formData.InstallationDate && formData.warrantyExpiry) {
      const installationDate = new Date(formData.InstallationDate);
      const warrantyExpiry = new Date(formData.warrantyExpiry);

      if (warrantyExpiry <= installationDate) {
        setValidationError(
          "Warranty Expiry must be greater than Installation Date"
        );
        toast.error("Warranty Expiry must be greater than Installation Date");
        return;
      }
    }

    if (mode === "create") {
      onSubmit?.(formData as AssetInventory);
    } else if (mode === "edit") {
      onSubmit?.(formData as AssetInventory);
    }
    onClose();
  };

  const handleCancel = () => {
    setFormData(defaultFormData);
    onClose();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (mode === "view") return;
    const { name, value } = e.target;
    setFormData((prev: AssetInventory) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "InstallationDate" || name === "warrantyExpiry") {
      setValidationError("");
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getMaxInstallationDate = () => {
    return getTodayDate();
  };
  const getMinWarrantyDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split("T")[0];

    if (formData.InstallationDate) {
      const installationDate = new Date(formData.InstallationDate);
      installationDate.setDate(installationDate.getDate() + 1);
      const dayAfterInstallation = installationDate.toISOString().split("T")[0];
      return dayAfterInstallation > tomorrowStr
        ? dayAfterInstallation
        : tomorrowStr;
    }
    // Otherwise, min is tomorrow (future only)
    return tomorrowStr;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {mode === "edit"
              ? "Edit Asset Inventory"
              : mode === "view"
              ? "View Asset Inventory"
              : "Add Asset Inventory"}
          </h2>
        </div>

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Asset ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asset ID
                </label>
                <input
                  type="text"
                  name="assetId"
                  value={formData.assetId || ""}
                  disabled={true}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                />
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  required={mode !== "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  required={mode !== "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Type</option>
                  {utilityAssetTypes.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.typeName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  required={mode !== "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Location</option>
                  {allLocations.map((department) => (
                    <option key={department._id} value={department._id}>
                      {department.room}
                    </option>
                  ))}
                </select>
              </div>

              {/* Purchase Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Date
                </label>
                <input
                  type="date"
                  name="purchaseDate"
                  value={formData.purchaseDate}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Installation Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Installation Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="InstallationDate"
                  value={formData.InstallationDate}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  required={mode !== "view"}
                  max={getMaxInstallationDate()}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Manufacturer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Manufacturer
                </label>
                <select
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Manufacturer</option>
                  {manufacturerList.map((manufacturer) => (
                    <option key={manufacturer._id} value={manufacturer._id}>
                      {manufacturer.manufacturerName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model
                </label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Warranty Expiry */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Warranty Expiry
                </label>
                <input
                  type="date"
                  name="warrantyExpiry"
                  value={formData.warrantyExpiry}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  min={getMinWarrantyDate()}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    validationError ? "border-red-500" : ""
                  } ${mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""}`}
                />
              </div>

              {/* Maintenance Vendor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maintenance Vendor (Optional)
                </label>
                <select
                  name="maintenanceVendor"
                  value={formData.maintenanceVendor}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Vendor</option>
                  {suppliers.map((supplier) => (
                    <option
                      key={supplier._id || supplier.id}
                      value={supplier._id}
                    >
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  required={mode !== "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-2 bg-[#61748e] border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center font-semibold gap-3 bg-[#869ab6] text-white px-4 py-2 rounded-full"
          >
            CANCEL
          </button>
          {mode === "view" ? null : (
            <button
              type="submit"
              onClick={handleSubmit}
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
            >
              {mode === "edit" ? "UPDATE" : "SUBMIT"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddAssetInventory;
